import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams, Link } from "react-router-dom";
import { cases } from "@/lib/endpoints";
import type { GateRequest, GateResponse } from "@/lib/api-types";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

interface Question {
  key: keyof GateRequest;
  text: string;
  helper: string;
  stepLabel: string;
  yesLabel: string;
  noLabel: string;
}

const QUESTIONS: Question[] = [
  {
    key: "continuous_us_residence",
    stepLabel: "Residency history",
    text: "Have you maintained continuous U.S. residence during the qualifying period?",
    helper: "A single trip of 6+ months breaks continuous residence. Multiple shorter trips can also add up — answer based on your full history.",
    yesLabel: "Yes, I have",
    noLabel: "No, I haven't",
  },
  {
    key: "meets_lpr_duration",
    stepLabel: "Green Card duration",
    text: "Have you held your Green Card for at least 5 years — or 3 years if married to a U.S. citizen?",
    helper: "Count from the date printed on your Permanent Resident Card, not the date you entered the U.S.",
    yesLabel: "Yes",
    noLabel: "Not yet",
  },
  {
    key: "currently_in_us",
    stepLabel: "Current location",
    text: "Are you physically inside the United States right now?",
    helper: "USCIS requires you to be present in the U.S. at the time of filing. You can return before submitting.",
    yesLabel: "Yes, I'm in the U.S.",
    noLabel: "No, I'm abroad",
  },
  {
    key: "is_self_filing",
    stepLabel: "Filing method",
    text: "Are you filing this application yourself, without an immigration attorney?",
    helper: "Our tool is built for self-filers. If you have an attorney, they should prepare and submit the form directly.",
    yesLabel: "Yes, self-filing",
    noLabel: "I have an attorney",
  },
  {
    key: "has_removal_proceedings",
    stepLabel: "Removal proceedings",
    text: "Are you currently in removal, deportation, or rescission proceedings?",
    helper: "Active removal proceedings make naturalization significantly more complex and are outside our current scope.",
    yesLabel: "Yes",
    noLabel: "No",
  },
  {
    key: "has_prior_uscis_denial",
    stepLabel: "Prior applications",
    text: "Has USCIS ever denied a previous application of yours — N-400 or otherwise?",
    helper: "Prior denials require additional documentation and review before we can assist.",
    yesLabel: "Yes",
    noLabel: "No",
  },
  {
    key: "has_criminal_history",
    stepLabel: "Criminal history",
    text: "Have you ever been arrested, charged, or convicted of any offense anywhere in the world?",
    helper: "Includes minor traffic citations, expunged records, and military discipline. Non-disclosure is more damaging than the incident — answer honestly.",
    yesLabel: "Yes",
    noLabel: "No",
  },
  {
    key: "married_to_usc",
    stepLabel: "Marital status",
    text: "Are you currently married to a U.S. citizen?",
    helper: "If yes, you may qualify under the 3-year rule. This question doesn't disqualify you either way.",
    yesLabel: "Yes",
    noLabel: "No",
  },
];

const TRUST_ITEMS = [
  { icon: LockIcon, label: "Bank-grade encryption" },
  { icon: UsersIcon, label: "2,412 applicants helped" },
  { icon: ScaleIcon, label: "USCIS Form N-400 · 2025" },
  { icon: ClipboardIcon, label: "Attorney-reviewed process" },
];

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ChatGatingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Partial<GateRequest>>({});
  const [isExiting, setIsExiting] = useState(false);
  const [result, setResult] = useState<GateResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const gateMut = useMutation({
    mutationFn: (req: GateRequest) => cases.gate(id!, req),
    onSuccess: (resp) => {
      setIsSubmitting(false);
      setResult(resp);
      if (resp.eligibility_status === "eligible") {
        setTimeout(() => navigate(`/cases/${id}`, { replace: true }), 1800);
      }
    },
    onError: () => setIsSubmitting(false),
  });

  function handleAnswer(key: keyof GateRequest, value: boolean) {
    if (isExiting) return;
    const newAnswers = { ...answers, [key]: value };
    setAnswers(newAnswers);
    setIsExiting(true);

    setTimeout(() => {
      const nextStep = currentStep + 1;
      if (nextStep >= QUESTIONS.length) {
        setIsSubmitting(true);
        setCurrentStep(nextStep);
        setIsExiting(false);
        gateMut.mutate(newAnswers as GateRequest);
      } else {
        setCurrentStep(nextStep);
        setIsExiting(false);
      }
    }, 220);
  }

  const progress = Math.round((currentStep / QUESTIONS.length) * 100);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 h-16 bg-white border-b border-slate-200/80 flex items-center px-6 gap-4 shadow-sm">
        {/* Logo */}
        <Link to="/dashboard" className="flex items-center gap-2.5 shrink-0">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-600 to-teal-500 flex items-center justify-center shadow-sm">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 1v14M1 8h14M4.5 4.5l7 7M11.5 4.5l-7 7" />
            </svg>
          </div>
          <span className="text-sm font-bold tracking-tight font-display text-slate-900 hidden sm:block">
            GetCitizen <span className="text-blue-600">AI</span>
          </span>
        </Link>

        {/* Center label */}
        <div className="flex-1 flex items-center justify-center gap-3">
          <span className="text-sm font-semibold text-slate-700 hidden sm:block">Eligibility Check</span>
          <span className="hidden sm:block text-slate-300">·</span>
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            About 90 seconds
          </span>
        </div>

        {/* Right: step counter */}
        <div className="shrink-0 flex items-center gap-2">
          {!result && !isSubmitting && (
            <span className="text-xs font-medium text-slate-500 tabular-nums">
              {Math.min(currentStep + 1, QUESTIONS.length)} <span className="text-slate-300">/</span> {QUESTIONS.length}
            </span>
          )}
          <Link
            to="/dashboard"
            className="text-xs text-slate-400 hover:text-slate-600 px-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            Exit
          </Link>
        </div>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left panel ─────────────────────────────────────────────── */}
        <aside className="hidden lg:flex flex-col w-72 shrink-0 bg-white border-r border-slate-200/80 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="flex-1 px-5 py-7">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4 px-1">
              Steps
            </p>
            <nav className="space-y-0.5">
              {QUESTIONS.map((q, i) => {
                const isDone = i < currentStep || !!result;
                const isCurrent = i === currentStep && !result && !isSubmitting;
                const isUpcoming = i > currentStep && !result;
                return (
                  <div
                    key={q.key}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors",
                      isCurrent && "bg-blue-50",
                      isDone && "opacity-70",
                    )}
                  >
                    {/* Step indicator */}
                    <div className={cn(
                      "w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold transition-all",
                      isDone && "bg-emerald-500 text-white",
                      isCurrent && "bg-blue-600 text-white shadow-sm",
                      isUpcoming && "bg-slate-100 text-slate-400",
                    )}>
                      {isDone ? "✓" : i + 1}
                    </div>
                    <span className={cn(
                      "font-medium leading-none",
                      isCurrent ? "text-blue-700" : isDone ? "text-slate-500" : "text-slate-400",
                    )}>
                      {q.stepLabel}
                    </span>
                  </div>
                );
              })}

              {/* Submitting state */}
              {isSubmitting && (
                <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-blue-50">
                  <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center shrink-0">
                    <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                  <span className="text-sm font-medium text-blue-700">Checking…</span>
                </div>
              )}
            </nav>
          </div>

          {/* Trust badges */}
          <div className="px-5 py-5 border-t border-slate-100">
            <div className="space-y-3">
              {TRUST_ITEMS.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2.5 text-xs text-slate-500">
                  <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  {label}
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* ── Right content ───────────────────────────────────────────── */}
        <main className="flex-1 flex flex-col items-center justify-start px-4 py-12 overflow-y-auto">
          <div className="w-full max-w-[560px]">

            {/* Result card */}
            {result && (
              <ResultCard result={result} caseId={id!} />
            )}

            {/* Submitting card */}
            {isSubmitting && !result && (
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-lg p-8 text-center animate-pop-in">
                <div className="w-12 h-12 rounded-full border-2 border-slate-200 border-t-blue-600 animate-spin mx-auto mb-4" />
                <h3 className="text-lg font-semibold font-display text-slate-900">Checking your eligibility…</h3>
                <p className="text-sm text-slate-500 mt-1.5">This only takes a second.</p>
              </div>
            )}

            {/* Question card */}
            {!result && !isSubmitting && (
              <div
                key={currentStep}
                className={cn(
                  "bg-white rounded-2xl border border-slate-200/80 shadow-lg p-8 transition-all duration-200",
                  isExiting ? "opacity-0 -translate-x-3" : "opacity-100 translate-x-0 animate-pop-in",
                )}
              >
                {/* Card top: badge + progress */}
                <div className="flex items-center justify-between mb-6">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
                    Question {currentStep + 1} of {QUESTIONS.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="w-28 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-600 rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-slate-400 tabular-nums w-6 text-right">
                      {progress}%
                    </span>
                  </div>
                </div>

                {/* Question text */}
                <h2 className="text-[22px] font-semibold font-display text-slate-900 leading-snug tracking-tight">
                  {QUESTIONS[currentStep].text}
                </h2>

                {/* Divider */}
                <div className="border-t border-slate-100 my-5" />

                {/* Helper text */}
                <p className="text-sm text-slate-500 leading-relaxed flex gap-2">
                  <svg className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {QUESTIONS[currentStep].helper}
                </p>

                {/* Answer buttons */}
                <div className="flex gap-3 mt-7">
                  <button
                    type="button"
                    disabled={isExiting}
                    onClick={() => handleAnswer(QUESTIONS[currentStep].key, false)}
                    className="flex-1 h-[52px] rounded-xl border-2 border-slate-200 bg-white text-slate-700 text-sm font-medium hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 active:scale-[0.98] transition-all duration-150 disabled:opacity-50"
                  >
                    {QUESTIONS[currentStep].noLabel}
                  </button>
                  <button
                    type="button"
                    disabled={isExiting}
                    onClick={() => handleAnswer(QUESTIONS[currentStep].key, true)}
                    className="flex-1 h-[52px] rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98] transition-all duration-150 disabled:opacity-50"
                  >
                    {QUESTIONS[currentStep].yesLabel}
                  </button>
                </div>
              </div>
            )}

            {/* Trust bar below card */}
            {!result && (
              <p className="text-xs text-slate-400 text-center mt-5 flex items-center justify-center gap-2">
                <LockIcon className="w-3 h-3" />
                Encrypted · Answers never shared · HIPAA-adjacent handling
              </p>
            )}

            {/* Error */}
            {gateMut.isError && (
              <p className="text-sm text-red-500 text-center mt-4">
                {gateMut.error?.message ?? "Something went wrong. Please try again."}
              </p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result card
// ---------------------------------------------------------------------------

function ResultCard({ result, caseId }: { result: GateResponse; caseId: string }) {
  const navigate = useNavigate();
  const status = result.eligibility_status;

  if (status === "eligible") {
    return (
      <div className="bg-white rounded-2xl border border-emerald-200 shadow-lg p-8 text-center animate-pop-in">
        <div className="w-14 h-14 rounded-2xl bg-emerald-500 flex items-center justify-center mx-auto mb-5 shadow-sm">
          <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold mb-4">
          Eligibility confirmed
        </div>
        <h2 className="text-2xl font-bold font-display text-slate-900 leading-snug">
          You're ready to apply
        </h2>
        <p className="text-sm text-slate-500 mt-2.5 leading-relaxed max-w-sm mx-auto">
          Based on your answers, you meet the basic requirements for U.S. naturalization. Taking you to your application now.
        </p>
        <button
          type="button"
          onClick={() => navigate(`/cases/${caseId}`, { replace: true })}
          className="mt-6 w-full h-[52px] rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 hover:-translate-y-0.5 hover:shadow-md transition-all duration-150"
        >
          Open my application →
        </button>
        <p className="text-xs text-slate-400 mt-3">Redirecting automatically…</p>
      </div>
    );
  }

  if (status === "manual_review") {
    return (
      <div className="bg-white rounded-2xl border border-amber-200 shadow-lg p-8 animate-pop-in">
        <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center mb-5">
          <svg className="w-7 h-7 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold mb-4">
          Attorney review needed
        </div>
        <h2 className="text-xl font-bold font-display text-slate-900">Your case needs a closer look</h2>
        <p className="text-sm text-slate-600 mt-2.5 leading-relaxed">
          {result.message ?? "Some details in your answers require review by a partner attorney before proceeding. We'll reach out with next steps."}
        </p>
        <Link
          to="/dashboard"
          className="mt-6 w-full h-[52px] rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors flex items-center justify-center"
        >
          Back to dashboard
        </Link>
      </div>
    );
  }

  // out_of_scope
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 animate-pop-in">
      <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-5">
        <svg className="w-7 h-7 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold mb-4">
        Not covered yet
      </div>
      <h2 className="text-xl font-bold font-display text-slate-900">We can't help with this case yet</h2>
      <p className="text-sm text-slate-600 mt-2.5 leading-relaxed">
        {result.disqualifying_question
          ? `Your situation around "${humanizeKey(result.disqualifying_question)}" is outside our current scope. We're focused on the most common N-400 paths first.`
          : "Your situation is outside our current scope. We're focused on the most common N-400 paths first — we'll notify you when we expand coverage."}
      </p>
      <Link
        to="/dashboard"
        className="mt-6 w-full h-[52px] rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors flex items-center justify-center"
      >
        Back to dashboard
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function humanizeKey(key: string) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Micro icon components (inline SVG, no dep)
function LockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}
function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}
function ScaleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
    </svg>
  );
}
function ClipboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  );
}
